import React, { useState, useMemo } from "react";
import { useCandidatess } from "../misc/useCandidate";
import { Skeleton, Table, TableProps } from "antd";
import { Candidate, iCandidates } from "../../../store/types/candidate";
import ModalNew from "../../../components/Modal/ModalNew";
import CreateCandidateModal from "../misc/CreateCandidateModal";
import ViewCandidateModal from "../misc/ViewCandidateModal";
import { FaEdit, FaEye, FaTrashAlt } from "react-icons/fa";
import { delete_candidate } from "../../../services/api/candidate";
import { useAppDispatch } from "../../../store/hooks";
import { useNavigate } from "react-router-dom";
import DeleteConfirmCandidateModal from "../../../components/elements/DeleteConfirmCandidateModal";
import SearchInput from "../../../components/elements/forms/SearchInput";
import { BiSearchAlt } from "react-icons/bi";

const CandidateHomepage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { candidates, loading, refetch } = useCandidatess();
  const [open, setOpen] = useState(false);
  const handleModalClose = () => {
    setOpen(false);
  };
  const [editModal, setEditModal] = useState({
    isOpen: false,
    candidate: null as Candidate | null,
  });
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    candidate: null as iCandidates | null,
  });
  const [openModal, setOpenModal] = useState({
    isOpen: false,
    candidate: null as Candidate | null,
  });
  const [searchTerm, setSearchTerm] = useState("");

  // Filter candidates based on search term
  const filteredCandidates = useMemo(() => {
    if (!searchTerm || !candidates) return candidates;

    const lowerSearch = searchTerm.toLowerCase();
    return candidates.filter(
      (candidate) =>
        candidate.name?.toLowerCase().includes(lowerSearch) ||
        candidate.email?.toLowerCase().includes(lowerSearch) ||
        candidate.jobTitle?.toLowerCase().includes(lowerSearch) ||
        candidate.phoneNumber?.toLowerCase().includes(lowerSearch)
    );
  }, [candidates, searchTerm]);

  // Confirm delete
  const handleConfirmDelete = () => {
    if (deleteModal.candidate) {
      console.log("Confirmed delete for:", deleteModal.candidate);
      delete_candidate(dispatch, deleteModal.candidate.id, navigate);

      // Close modal
      setDeleteModal({ isOpen: false, candidate: null });
    }
  };

  // Cancel delete
  const handleCancelDelete = () => {
    console.log("Delete cancelled");
    setDeleteModal({ isOpen: false, candidate: null });
  };

  // Handle successful candidate creation
  const handleCreateSuccess = () => {
    // Refetch the candidates list
    refetch();
    // Close the modal
    setOpen(false);
  };

  // Handle successful candidate update
  const handleEditSuccess = () => {
    // Refetch the candidates list
    refetch();
    // Close the modal
    setEditModal({ isOpen: false, candidate: null });
  };

  const columns: TableProps<iCandidates>["columns"] = [
    {
      title: "S/N",
      dataIndex: "key",
      width: 20,
      align: "center",
      render: (_, __, index) => index + 1,
      // rowScope: 'row',
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      // render: (_, record) => record.name,
      // Array.isArray(record.data),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      // render: (_, record) => record.email,
    },
    {
      title: "Profession",
      dataIndex: "jobTitle",
      key: "jobTitle",
      render: (_, record) =>
        record.jobTitle
          ?.replace(/_+/g, " ")
          .toLowerCase()
          ?.replace(/\b\w/g, (char) => char.toUpperCase()),
    },
    {
      title: "Phone Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      // render: (_, record) => record.phoneNumber,
    },
    {
      title: "Date Created",
      dataIndex: "createdAt",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      // width: 180,
      align: "center",
      render: (_, record) => (
        <div className="flex items-center justify-center">
          <div
            onClick={() => {
              setOpenModal({
                isOpen: true,
                candidate: record,
              });
            }}
            className="cursor-pointer"
          >
            <FaEye className="text-[blue] text-[18px]" />
          </div>
          <div className="w-[12px]"></div>
          <div
            onClick={() => {
              setEditModal({
                isOpen: true,
                candidate: record,
              });
            }}
            className="cursor-pointer"
          >
            <FaEdit className="text-[green] text-[18px]" />
          </div>
          <div className="w-[12px]"></div>
          <div
            onClick={() => {
              console.log("Delete button clicked for:", record);
              setDeleteModal({
                isOpen: true,
                candidate: record,
              });
              // delete_job(dispatch, record.id);
            }}
            className="cursor-pointer"
          >
            <FaTrashAlt className="text-[red] text-[18px]" />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="w-full py-5">
      <div className="max-w-[1096px] mx-auto px-3">
        <div className="flex items-center justify-between">
          <p className="font-[600]">Newly Created Candidates</p>
          <div className="flex items-center gap-3">
            <SearchInput
              className="w-full max-w-[280px]"
              name="search"
              value={searchTerm}
              placeholder="Search by name, email..."
              icon={<BiSearchAlt size={18} color="#243677" />}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div
              className="h-[40px] w-[210px] flex items-center justify-center bg-secondary text-white rounded-8 cursor-pointer"
              onClick={() => setOpen(true)}
            >
              Add New Candidate
            </div>
          </div>
        </div>

        {loading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : candidates ? (
          <Table<iCandidates>
            dataSource={
              filteredCandidates.map((c) => ({
                ...c,
                deletedAt: c.deletedAt ?? "",
              })) as iCandidates[]
            }
            columns={columns}
            loading={loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} Candidates`,
            }}
          />
        ) : (
          <div className="text-center text-gray-500">
            Contents failed to load, please try again.
          </div>
        )}

        <DeleteConfirmCandidateModal
          title="candidate"
          candidate={deleteModal.candidate}
          isOpen={deleteModal.isOpen}
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      </div>
      <ModalNew isOpen={open} onClose={handleModalClose} title="Add Candidate">
        <CreateCandidateModal onSuccess={handleCreateSuccess} />
      </ModalNew>

      {/* Edit Candidate Modal */}
      <ModalNew
        isOpen={editModal.isOpen}
        onClose={() => setEditModal({ isOpen: false, candidate: null })}
        title="Edit Candidate"
      >
        <CreateCandidateModal
          candidateData={editModal.candidate}
          isEditMode={true}
          onSuccess={handleEditSuccess}
        />
      </ModalNew>
      {/* Open Candidate Modal */}
      <ModalNew
        isOpen={openModal.isOpen}
        onClose={() => setOpenModal({ isOpen: false, candidate: null })}
        title="View Candidate"
      >
        <ViewCandidateModal
          candidateData={openModal.candidate}
          onClose={() => setOpenModal({ isOpen: false, candidate: null })}
        />
      </ModalNew>
    </div>
  );
};

export default CandidateHomepage;
